import { useMemo, useRef, useState } from 'react';
import { cn, useOutsideClick } from 'common/utils';
import { CaretIcon, XIcon } from 'components/v2/atoms/icon';
import SymbolSelectionDialog from 'components/v2/molecules/symbol-selection-dialog';
import { useQuery } from 'react-query';
import { getAllSymbols } from 'service/chart';
import useGlobalStore from 'store/global';

const MAX_SYM = 3;

const ChooseSymbol = ({
  symbolIds,
  setSymbolIds,
}: {
  symbolIds: number[];
  setSymbolIds: (symbolIds: number[]) => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);

  useOutsideClick(containerRef, () => {
    if (isModalOpen) setIsModalOpen(false);
  });
  const { data } = useQuery(
    'getSymbolsList',
    async () => {
      return await getAllSymbols();
    },
    {
      refetchOnWindowFocus: false,
      enabled: isAuthInit,
      staleTime: 15 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
    },
  );

  const selectedSymbols = useMemo(() => {
    if (!data?.data || symbolIds.length === 0) return [];
    return data?.data?.filter((s) => symbolIds.includes(s.id)) || [];
  }, [symbolIds, data?.data]);

  const handleSymbolRemove = (symbolId: number) => {
    setSymbolIds(symbolIds.filter((id) => id !== symbolId));
  };

  const handleSymbolsSelect = (symbolsIds: number[]) => {
    setSymbolIds(symbolsIds);
  };

  return (
    <div ref={containerRef}>
      <div
        className="cursor-pointer rounded-lg bg-pp-bg-tertiary px-4 py-3 hover:bg-pp-border-primary"
        onClick={() => setIsModalOpen(!isModalOpen)}
      >
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-[15px] font-normal text-pp-text-primary">
            マーケット:
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {selectedSymbols.length === 0 ? (
              <span className="text-sm text-green-primary hover:font-bold">
                追加する +
              </span>
            ) : (
              <>
                {selectedSymbols.map((symbol) => (
                  <div
                    key={symbol.id}
                    className="flex items-center space-x-1 rounded-2xl border bg-growth-green-subtle px-2.5 py-1 text-[10px] font-bold leading-[15px] text-green-primary"
                  >
                    <span>{symbol.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSymbolRemove(symbol.id);
                      }}
                      className="text-green-primary"
                    >
                      <XIcon className="size-3 hover:opacity-50" />
                    </button>
                  </div>
                ))}
                {selectedSymbols.length < MAX_SYM && (
                  <span className="text-sm text-green-primary hover:font-bold">
                    +
                  </span>
                )}
              </>
            )}
          </div>
          <CaretIcon
            className={cn(
              'ml-auto size-4 text-pp-text-tertiary transition-transform',
              {
                'rotate-180': isModalOpen,
              },
            )}
          />
        </div>
      </div>

      <SymbolSelectionDialog
        isOpen={isModalOpen}
        onSelect={handleSymbolsSelect}
        selectedSymbols={selectedSymbols}
        allSymbols={data?.data || []}
        triggerRef={containerRef}
      />
    </div>
  );
};

export default ChooseSymbol;
